/**
 * Types and labels for the "Order Check" tab on /docuware-invoices.
 *
 * The finding CODE is the contract with the API; the label and the explanation live here and
 * nowhere else. That split is the point: a rule can be reworded for a reader without touching
 * the checker, and the checker can add a rule without the UI guessing at what to call it.
 */

export type PoCheckVerdict = 'Ok' | 'Warning' | 'Error' | 'Unchecked';
export type PoCheckSeverity = 'Error' | 'Warning' | 'Info';
export type PoReviewStatus = 'Open' | 'CheckedOk' | 'Disputed';
export type PoResolution = 'open' | 'posted' | 'multi' | 'none' | 'notapplicable';

export interface PoCheckRow {
    documentId: number;
    companyCode: string;
    supplier: string | null;
    invoiceNumber: string | null;
    invoiceDate: string | null;
    amount: number | null;
    currency: string | null;
    storedAt: string | null;
    docId: number | null;
    fileCabinetId: string | null;
    poNo: string | null;
    poNoRaw: string | null;
    poResolution: PoResolution;
    expectedRegime: string | null;
    /** 'order' | 'posting' | null — where expectedRegime was read from. */
    vatSource: string | null;
    actualRegime: string | null;
    actualCounterpartyBloc: string | null;
    verdict: PoCheckVerdict;
    findingCount: number;
    errorCount: number;
    extractionStatus: string | null;
    checkedAt: string | null;
    reviewStatus: PoReviewStatus;
    reviewedBy: string | null;
    reviewedAt: string | null;
    topFindingCode: string | null;
    /** The BC posting, so a reviewer can see whether the finding is already in the ledger. */
    bcDocumentNo: string | null;
    bcDocumentKind: 'invoice' | 'creditmemo' | 'ledger' | null;
    // --- link back to the archived document, decided server-side (same three states as the
    // invoice table). A finding you cannot open the invoice for is not actionable.
    documentUrl: string | null;
    documentUrlVerified: boolean;
    baseUrl: string | null;
}

/** The coarse buckets over the rules. Mirrors FINDING_CATEGORY in the API's po-check types. */
export type PoFindingCategory = 'vat' | 'order' | 'pricing' | 'quantity' | 'document';

export interface PoCheckFinding {
    id: number;
    code: string;
    severity: PoCheckSeverity;
    lineRef: string | null;
    expected: string | null;
    actual: string | null;
    message: string;
    /** Set server-side from the one mapping, so the panel and the filter cannot disagree. */
    category: PoFindingCategory | null;
    categoryLabel: string | null;
}

export interface PoCheckLine {
    sortOrder: number;
    matchBasis: string;
    poLineNo: number | null;
    poItemNo: string | null;
    poDescription: string | null;
    poQuantity: number | null;
    poUom: string | null;
    poUnitCost: number | null;
    poLineAmount: number | null;
    poQtyReceived: number | null;
    poVatProdGroup: string | null;
    poVatPct: number | null;
    invLineNo: number | null;
    invItemNo: string | null;
    invDescription: string | null;
    invQuantity: number | null;
    invUom: string | null;
    invUnitPrice: number | null;
    invLineAmount: number | null;
    invVatCode: string | null;
    invVatPct: number | null;
}

export interface PoCheckDetail {
    check: PoCheckRow & {
        invoiceCategory: string | null;
        indexPurchaseOrderNo: string | null;
        indexVendorNo: string | null;
        extractionModel: string | null;
        extractionError: string | null;
        extractedAt: string | null;
        reviewComment: string | null;
        bcPostingDate: string | null;
        bcDocumentType: string | null;
        /**
         * EVERY posting this document became, primary first (migration 192).
         *
         * The check itself keeps reading the primary - it needs exactly one booked invoice -
         * but the panel must not print '842036' for a document that is 842036 AND 842037.
         * More than one posting is also why the comparison ran against the ORDER rather than
         * the booked invoice, so the list is the explanation for the verdict beside it.
         */
        bcPostings: string | null;
    };
    findings: PoCheckFinding[];
    lines: PoCheckLine[];
}

export interface PoCheckSummary {
    verdicts: Partial<Record<PoCheckVerdict, number>>;
    /**
     * Counted WITHOUT the code filter itself, so picking a code does not collapse the dropdown
     * to that one code. `category` travels with each entry — the tab narrows the code list to
     * the chosen category from this, rather than holding a second copy of the mapping.
     */
    findingCodes: { code: string; severity: PoCheckSeverity; docs: number; category: PoFindingCategory | null }[];
    /**
     * Coarse buckets, each with its OWN COUNT(DISTINCT DocumentId) — summing the per-code
     * counts would double-count a document that trips two VAT rules. Empty categories are
     * omitted, so the dropdown never offers a filter that leads nowhere.
     */
    findingCategories: { category: PoFindingCategory; label: string; docs: number }[];
    /** Every company with checks, unfiltered — a company filter has to be switchable. */
    companies: string[];
    lastCheckedAt: string | null;
}

export interface PoCheckListResult {
    items: PoCheckRow[];
    summary: PoCheckSummary;
    total: number;
}

export interface PoCheckFilters {
    companyCode: string;
    verdict: string;
    reviewStatus: string;
    poResolution: string;
    findingCategory: string;
    findingCode: string;
    search: string;
    storedFrom: string;
    storedTo: string;
}

export const EMPTY_PO_CHECK_FILTERS: PoCheckFilters = {
    companyCode: '', verdict: '', reviewStatus: '', poResolution: '',
    findingCategory: '', findingCode: '', search: '', storedFrom: '', storedTo: '',
};

export type PoCheckSortField =
    | 'verdict' | 'checked' | 'stored' | 'supplier' | 'invoiceNumber'
    | 'invoiceDate' | 'amount' | 'poNo' | 'errorCount' | 'reviewStatus';

/**
 * What each rule means, in the words a reviewer needs.
 *
 * `label` is the chip in the table; `explain` is the sentence under it in the detail panel.
 * The explanations say what to DO about a finding where that is not obvious, because the
 * whole point of the tab is that the interesting problems are the ones whose numbers agree.
 */
export const FINDING_LABELS: Record<string, { label: string; explain: string }> = {
    PO_NOT_FOUND: {
        label: 'Order not found',
        explain: 'The invoice names an order number that does not exist in Business Central, open or posted. Usually a typo in the DocuWare index field, occasionally an order that was never placed.',
    },
    PO_MISSING_ON_INVOICE: {
        label: 'No order number on the invoice',
        explain: 'The document itself does not quote our purchase-order number. The supplier was working from something other than our order, which is how the wrong terms get onto an invoice in the first place.',
    },
    PO_MULTIPLE: {
        label: 'Several orders',
        explain: 'The invoice is filed against more than one purchase order, so lines cannot be compared one to one. Header checks still ran.',
    },
    VENDOR_MISMATCH: {
        label: 'Wrong vendor',
        explain: 'The invoice is filed under a different vendor than the one the order was placed with.',
    },
    CURRENCY_MISMATCH: {
        label: 'Currency differs',
        explain: 'Invoice currency does not match the order currency.',
    },
    TOTAL_MISMATCH: {
        label: 'Total differs',
        explain: 'Priced at the order rates, the quantities on this invoice do not add up to the invoiced total. Partial deliveries are already accounted for.',
    },
    QTY_MISMATCH: {
        label: 'Over-delivered',
        explain: 'More is invoiced than was ordered on that line. Invoicing less is normal and is not reported.',
    },
    PRICE_MISMATCH: {
        label: 'Price differs',
        explain: 'The unit price differs from the order, after converting both sides to the order unit.',
    },
    LINE_AMOUNT_MISMATCH: {
        label: 'Line amount differs',
        explain: 'The line total is not the ordered price times the invoiced quantity.',
    },
    QTY_EXCEEDS_RECEIPT: {
        label: 'More invoiced than received',
        explain: 'The invoiced quantity is greater than what has been booked in as received against this order line. Shown as information only when nothing has been received yet — an invoice that arrives before the receipt is posted is normal, not a supplier error.',
    },
    LINE_UNMATCHED_INVOICE: {
        label: 'Extra invoice line',
        explain: 'A line on the invoice has no counterpart on the order — often a surcharge, freight or inflation line that was never agreed.',
    },
    LINE_UNMATCHED_PO: {
        label: 'Order line not invoiced',
        explain: 'An order line this invoice does not cover. Normal for a partial delivery; shown so the whole order is visible.',
    },
    VAT_REGIME_MISMATCH: {
        label: 'Wrong VAT treatment',
        explain: 'The legal basis printed on the invoice contradicts the VAT treatment the order was placed under. Every amount can still be correct — this is about which tax regime the supply falls into.',
    },
    VAT_GEOGRAPHY_CONTRADICTION: {
        label: 'VAT contradicts the delivery',
        explain: 'Where the goods physically go rules out the exemption the invoice claims. Goods that stay inside the EU cannot be an export, and goods that leave it cannot be an intra-Community supply.',
    },
    BILLTO_VATID_MISMATCH: {
        label: 'Wrong VAT registration billed',
        explain: 'The invoice is addressed to a different VAT registration than the one the order runs under. This is usually the root cause: the supplier picked the wrong customer record and the VAT treatment followed it.',
    },
    VAT_RATE_MISMATCH: {
        label: 'VAT charged differs',
        explain: 'The order expects VAT at one rate and the invoice charges another — or charges none where it should, or some where it should not.',
    },
    BILLTO_VATID_UNREADABLE: {
        label: 'No VAT number found for us',
        explain: 'Nothing that looks like a VAT registration number for us could be read on this invoice, so the registration was not compared. The value found in its place — often a layout marker or a customer number — is shown so you can check the document yourself.',
    },
    VAT_ARITHMETIC: {
        label: 'Document does not add up',
        explain: 'Net, plus any freight or surcharge, minus any cash discount, plus VAT does not reach the total the document prints. The components used are listed, so a gap means either a real error or a component we could not read.',
    },
    VAT_CLAUSE_UNKNOWN: {
        label: 'VAT clause not recognised',
        explain: 'The legal wording on this invoice matches no known pattern, so no conclusion was drawn from it. Nothing is assumed — the clause is shown as printed and the pattern table can be widened.',
    },
    SERIES_ANOMALY: {
        label: 'Unusual invoice series',
        explain: 'This supplier has never used this invoice-number series for this kind of order before. Suppliers normally keep one number series per VAT regime, so a break in the pattern is worth a look.',
    },
    BUNDLE_INVOICE_NOT_FOUND: {
        label: 'Bundle contents',
        explain: 'The archived document holds more than the invoice it is indexed under, or the invoice could not be located inside it.',
    },
    EXTRACTION_FAILED: {
        label: 'Could not read the document',
        explain: 'The PDF could not be downloaded or read. Nothing about the invoice was checked; re-run once the cause is cleared.',
    },
};

export function findingLabel(code: string): string {
    return FINDING_LABELS[code]?.label ?? code;
}

/** Buyer-side and seller-side regime names, spelled out for a reader. */
export const REGIME_LABELS: Record<string, string> = {
    INTRA_EU_ACQUISITION: 'Intra-EU acquisition',
    INTRA_EU_SUPPLY: 'Intra-EU supply',
    DOMESTIC: 'Domestic VAT',
    IMPORT: 'Import',
    EXPORT: 'Export',
    REVERSE_CHARGE: 'Reverse charge',
    OUT_OF_SCOPE: 'Out of scope',
    TRIANGULAR: 'Triangular (art. 141)',
    UNKNOWN: 'Not determined',
};

/**
 * Which source the VAT expectation was read from.
 *
 * Shown next to it, because "the invoice contradicts the order" and "the invoice contradicts
 * how we booked it" are different claims, and a reviewer has to be able to tell them apart
 * before taking one to a supplier.
 */
export const VAT_SOURCE_LABELS: Record<string, string> = {
    order: 'from the order',
    posting: 'from the posting',
};

export const RESOLUTION_LABELS: Record<PoResolution, string> = {
    open: 'open order',
    posted: 'posted invoice',
    multi: 'several orders',
    none: 'not found',
    notapplicable: 'no order number',
};
